import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { reconciliationActs, type ReconciliationAct } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'documents.reconciliation_acts.create';

const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Чернетка',
  SENT: 'Відправлено',
  AGREED: 'Узгоджено',
  DISPUTED: 'Спірний',
};

export default function ReconciliationActsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );
  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['reconciliation-acts', orgId],
    queryFn: () => reconciliationActs.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<ReconciliationAct>[]>(
    () => [
      { key: 'document_number', header: 'Номер', width: 130, importBadge: true,
        sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'document_date', header: 'Дата', width: 110,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => new Date(r.document_date).toLocaleDateString('uk-UA') },
      { key: 'period_start', header: 'Період з', width: 110,
        sortValue: (r) => r.period_start ?? '', cell: (r) => r.period_start },
      { key: 'period_end', header: 'по', width: 110,
        sortValue: (r) => r.period_end ?? '', cell: (r) => r.period_end },
      { key: 'closing_balance_our', header: 'Баланс (наш)', width: 140, align: 'right',
        sortValue: (r) => Number(r.closing_balance_our), cell: (r) => r.closing_balance_our },
      { key: 'closing_balance_their', header: 'Баланс (контр.)', width: 150, align: 'right',
        sortValue: (r) => Number(r.closing_balance_their ?? 0), cell: (r) => r.closing_balance_their ?? '' },
      { key: 'discrepancy_amount', header: 'Розходження', width: 130, align: 'right',
        sortValue: (r) => Number(r.discrepancy_amount), cell: (r) => r.discrepancy_amount },
      { key: 'state', header: 'Стан', width: 130,
        sortValue: (r) => r.state ?? '', cell: (r) => STATE_LABEL[r.state] ?? r.state },
    ],
    [],
  );
  const canCreate = !!orgId && allowedOrgIds.has(orgId);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Акти звірки</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
          ))}
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          onClick={() => navigate(`/settlements/reconciliation-acts/new?org=${orgId}`)}
        >
          + Акт звірки
        </button>
      </div>
      <DataTable<ReconciliationAct>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/settlements/reconciliation-acts/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
